import React, { useMemo, memo, useEffect, useReducer, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';
import { LANGUAGES } from 'lib/helpers';
import { createField, updateExtraFieldTab } from 'lib/services/fields';
import { showErrorToast } from 'lib/errors';

import ForceForm, { validate, flatList, getDefaults } from 'components/ForceForm';
import HyperDrawer from 'components/HyperDrawer';
import { getDescription } from 'utils/custom';
import { successToast, errorToast } from 'utils/toast';

import styles from './styles';
const useStyles = createUseStyles(styles, 'FormTab');

const FORM_SCHEMA = {
    required: ['entity', 'description'],
    properties: {
        entity: {
            maxLength: 200,
            type: ['string'],
        },
        description: {
            maxLength: 200,
            type: ['string'],
        },
    },
    layout: {
        id: 'createTab',
        uiGrid: 'linearVertical',
        composition: [
            {
                schemaId: 'entity',
                label: 'cfm_label_related_table',
                uiType: 'list',
                placeholder: 'cfm_placeholder_type_value',
            },
            {
                schemaId: 'description',
                label: 'cfm_label_description',
                uiType: 'expandableComponent',
                labelForExpand: ['cfm_label_view_more_lang', 'cfm_label_view_less_lang'],
                composition: [
                    {
                        schemaId: 'description',
                        uiType: 'text',
                        label: 'cfm_label_description',
                        placeholder: 'cfm_label_spanish',
                    },
                    ...LANGUAGES.map((language) => ({
                        label: `cfm_label_${language.label}`,
                        schemaId: `description${language.key}`,
                        uiType: 'text',
                        placeholder: 'cfm_placeholder_type_value',
                    })),
                ],
            },
        ],
    },
};

const entitiesLiterals = {
    Empresas: 'cfm_label_accounts',
    Contactos: 'cfm_label_contacts',
    Expedientes: 'cfm_label_opportunities',
    Gestiones: 'cfm_label_activities',
    tblAgenda: 'cfm_label_event_task',
    tblSalesOrders: 'cfm_label_salesorders',
    Productos: 'cfm_label_products',
    tblUsuarios: 'cfm_label_users',
};

const initialState = {
    data: {},
    originalData: {},
    list: [],
    tabOptions: [],
    errors: {},
    isOpen: false,
    isLoading: true,
    isSaving: false,
    schema: FORM_SCHEMA,
};

function reducer(state, action) {
    switch (action.type) {
        case 'init':
            return {
                ...state,
                originalData: action.data,
                data: action.data,
                list: action.list,
                tabOptions: action.tabOptions,
                errors: {},
                isOpen: true,
                isLoading: false,
            };
        case 'close':
            return { ...state, isSaving: false, isOpen: false };
        case 'changeData':
            return { ...state, data: action.data };
        case 'setErrors':
            return { ...state, isSaving: false, errors: action.errors };
        case 'setSaving':
            return {
                ...state,
                isSaving: action.value,
            };
        case 'setLoading':
            return {
                ...state,
                isLoading: action.value,
            };
        default:
            throw new Error('No action provided');
    }
}

const FormTab = ({
    onRef,
    refreshList,
    idSetupRole,
    setActiveRowId,
    overrides,
    onStack,
    afterOnSave,
}) => {
    const { t, fieldsToHide } = useWeb5Context({ overrides });
    const [state, dispatch] = useReducer(reducer, initialState);
    const formFields = useRef();
    const classes = useStyles();
    const dumbUser = idSetupRole ? [8, 9].includes(idSetupRole) : false;

    // Child method
    useEffect(() => {
        onRef &&
            onRef({
                open({ data, entities, entity }) {
                    const overrideIsHidden = (id) =>
                        Array.isArray(fieldsToHide) && fieldsToHide.includes(id);

                    formFields.current = JSON.parse(JSON.stringify(FORM_SCHEMA));
                    formFields.current = fieldsToHide?.length
                        ? {
                              ...FORM_SCHEMA,
                              layout: {
                                  ...FORM_SCHEMA.layout,
                                  composition: FORM_SCHEMA.layout.composition.map((field) => {
                                      field.isHidden =
                                          overrideIsHidden(field.schemaId || field.id) ||
                                          field.isHidden;
                                      return field;
                                  }),
                              },
                          }
                        : formFields.current;

                    const loadOptions = entities?.map((option) => {
                        const customLiteral = t(entitiesLiterals[option.name]) || null;
                        return {
                            label: getDescription({ tab: option, dumbUser, customLiteral }),
                            value: option.name,
                        };
                    });
                    const finalEntity = loadOptions?.find((option) => option.value === entity);

                    if (data) {
                        dispatch({
                            type: 'init',
                            list: entities,
                            tabOptions: loadOptions,
                            data: {
                                ...data,
                                isNew: false,
                                entity: finalEntity || {
                                    label: t(entitiesLiterals[entity]),
                                    value: entity,
                                },
                            },
                        });
                    } else {
                        dispatch({
                            type: 'init',
                            list: entities,
                            tabOptions: loadOptions,
                            data: {
                                ...getDefaults(FORM_SCHEMA),
                                isNew: true,
                                entity: finalEntity || {
                                    label: t(entitiesLiterals[entity]),
                                    value: entity,
                                },
                            },
                        });
                    }
                    dispatch({ type: 'setLoading', value: false });
                },
            });
    }, [onRef, state, t, dumbUser, fieldsToHide]);

    const onSave = useCallback(() => {
        validate(state.data, formFields.current).then(({ isValid, errors, cleanData }) => {
            if (!isValid) {
                dispatch({ type: 'setErrors', errors });
            } else {
                dispatch({ type: 'setSaving', value: true });

                let finalCleanData = flatList(cleanData, state.schema);

                delete finalCleanData.order;

                const fieldTabCreation = state?.data?.id
                    ? updateExtraFieldTab(state.data.id, finalCleanData)
                    : createField(finalCleanData);

                fieldTabCreation
                    .then((response) => {
                        if (!response?.data) {
                            dispatch({ type: 'close' });
                            errorToast();
                            return;
                        }

                        const value = {
                            label: response.data.description,
                            value: response.data.id,
                        };

                        afterOnSave && afterOnSave(value);
                        refreshList && refreshList();
                        setActiveRowId?.(null);
                        dispatch({ type: 'close' });
                        successToast({
                            t,
                            title: t(
                                state.data.isNew
                                    ? 'cfm_success_create_tab'
                                    : 'cfm_success_update_tab',
                            ),
                        });

                        onStack && onStack(1);
                    })
                    .catch((error) => {
                        console.error(error);
                        showErrorToast(t, error);
                        dispatch({ type: 'setErrors', errors: error });
                        onStack && onStack(1);
                    });
            }
        });
    }, [state, refreshList, setActiveRowId, t, onStack, afterOnSave]);

    const locales = useMemo(() => {
        return {
            title: state?.data?.id ? t('cfm_title_edit_tab') : t('cfm_title_create_tab'),
            cancel: t('cfm_action_cancel'),
            save: t('cfm_action_save'),
        };
    }, [t, state]);

    const modifyFieldProps = useCallback(
        (field) => {
            let newField = field;
            switch (field.schemaId || field.id) {
                case 'entity':
                    newField = {
                        ...newField,
                        options: state.tabOptions,
                    };
                    break;
                default:
                    break;
            }
            return newField;
        },
        [state.tabOptions],
    );

    const onCancel = useCallback(() => {
        setActiveRowId?.(null);
        dispatch({ type: 'close' });
        onStack && onStack(1);
    }, [setActiveRowId, onStack]);

    const onChange = useCallback((data) => {
        dispatch({ type: 'changeData', data });
    }, []);

    return (
        <HyperDrawer
            isOpen={state.isOpen}
            locales={locales}
            isLoading={state.isLoading}
            isSaving={state.isSaving}
            onCancel={onCancel}
            onSave={onSave}
            data={state.data}
            originalData={state.originalData}
            className="fields__createtab-form"
        >
            <ForceForm
                values={state.data}
                errors={state.errors}
                onChange={onChange}
                schema={formFields.current}
                mode={state?.data?.id ? 'update' : 'create'}
                className={classes.root}
                modifyFieldProps={modifyFieldProps}
            />
        </HyperDrawer>
    );
};

FormTab.propTypes = {
    onRef: PropTypes.func.isRequired,
    refreshList: PropTypes.func.isRequired,
};

export default memo(FormTab);
